import Report from "@/types/Report/Report";
import UseCaseResponse from "@/types/UseCaseResponse";
import axios from "axios";
import BaseApiService from "./BaseApiService";

/** Read blob body as UTF-8 text (Blob.prototype.text() is missing from TS 3.5 DOM typings). */
function readBlobAsText(blob: Blob): Promise<string> {
  return new Response(blob).text();
}

/** Large registration-style exports can exceed default proxy/API durations; client must wait long enough. */
const REPORT_POST_TIMEOUT_MS = 900000;

export default class ReportApiService extends BaseApiService {
  /**
   * Runs a server-side report and downloads the spreadsheet.
   * Large datasets (many sites / active season) need a long-running POST; failures surface as timeouts or blob JSON errors.
   */
  async RunReport(reportName: string, params: any): Promise<boolean> {
    const fileName = reportName;
    await this.defineHeader();

    try {
      const response = await axios.post(
        `${this.url}/api/vms/Report/${reportName}`,
        params,
        {
          responseType: "blob",
          timeout: REPORT_POST_TIMEOUT_MS
        }
      );

      const blob = response.data as Blob;
      const ct = (response.headers["content-type"] || "").toLowerCase();

      if (ct.includes("application/json")) {
        const text = await readBlobAsText(blob);
        try {
          const json = JSON.parse(text);
          console.error("Report API returned JSON instead of a file:", json);
        } catch {
          console.error("Report API returned unexpected JSON body");
        }
        return false;
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${fileName}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      return true;
    } catch (err) {
      if (
        axios.isAxiosError(err) &&
        err.response &&
        err.response.data instanceof Blob
      ) {
        try {
          const text = await readBlobAsText(err.response.data);
          const json = JSON.parse(text);
          console.error("Report API error:", json);
        } catch {
          console.error(err);
        }
      } else {
        console.error(err);
      }
      return false;
    }
  }

  async GetReports(): Promise<UseCaseResponse<Report[]>> {
    await this.defineHeader();
    return axios
      .get(`${this.url}/api/vms/Report`)
      .then(resp => resp.data as UseCaseResponse<Report[]>);
  }

  async GetDashboardData(site_id: string): Promise<UseCaseResponse<object>> {
    await this.defineHeader();
    return axios
      .get(`${this.url}/api/vms/Report/Dashboard?site_id=${site_id}`)
      .then(resp => resp.data as UseCaseResponse<object>);
  }
}
